import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { langCodeToJapanese } from "@/lib/chillnn/lang-code";
import type { HotelI18n_DesignPageQuery, LangCode } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";

import { ToastAction } from "@/components/ui/toast/toast";

import * as Shared from "../../../../../_shared";
import { useHotelI18nArticleUpdateMutation } from "../../repository/mutation/use-hotel-i18n-article-update-mutation";

type Props = {
	hotelID: string;
	langCode: LangCode;
	data: HotelI18n_DesignPageQuery;
};

/**
 * @todo キャッシュされているので許容できるが、２回取得の処理が走っているので対応する
 */
export const useInitialized = ({ hotelID, langCode, data }: Props) => {
	const { article } = data.hotelI18n;
	const { onUpdate: _onUpdate } = useHotelI18nArticleUpdateMutation({ hotelID, langCode });

	const navigate = useNavigate();
	const hotelListPath = generatePath(PATHS.HOTEL.RESERVATION_PAGE.MAGAZINE.LIST.HOTEL, { hotelID });

	const onNavigateToHotelList = useCallback(() => {
		navigate(hotelListPath);
	}, [hotelListPath, navigate]);

	const onUpdateSuccessCallback = useCallback(() => {
		const langCodeText = langCodeToJapanese(langCode);

		notifyUserEventInfo({
			title: `施設記事（${langCodeText}）を保存しました`,
			action: (
				<ToastAction onClick={onNavigateToHotelList} altText="一覧に戻る">
					一覧に戻る
				</ToastAction>
			),
		});
	}, [langCode, onNavigateToHotelList]);

	const { articleHTML, onSetArticleHTML, isDirty, isValid, onUpdate } =
		Shared.Model.UseCase.useCreateMagazineEditorForI18nModel({
			article,
			onUpdate: _onUpdate,
			onUpdateSuccessCallback,
		});

	return {
		data,
		articleHTML,
		onSetArticleHTML,
		isDirty,
		isValid,
		onUpdate,
	};
};
